import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faFileCsv,
  faFileExcel,
  faSort,
  faSortUp,
  faSortDown,
  faRefresh
} from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../QueueFull/cabinet-zdo-requests.css';
import './vacancy-report.css';

// Импорт API функций
import { vacancyReportAPI } from '../StaffScheduleList/actions';

const { getVacancyReport, exportVacancyReport } = vacancyReportAPI;

const VacancyReport = ({ kgId = '' }) => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'positionName', direction: 'asc' });
  const [exporting, setExporting] = useState(false);

  const columns = [
    {
      key: 'positionName',
      label: 'Найменування посади',
      sortable: true,
      width: '40%'
    },
    {
      key: 'positionType',
      label: 'Тип посади',
      sortable: true,
      width: '20%'
    },
    {
      key: 'totalUnits',
      label: 'Всього',
      sortable: true,
      width: '15%'
    },
    {
      key: 'occupiedUnits',
      label: 'Фактично у штаті',
      sortable: true,
      width: '15%'
    },
    {
      key: 'vacantUnits',
      label: 'Вакантних місць',
      sortable: true,
      width: '15%'
    }
  ];

  // Загрузка отчета
  const loadReport = async () => {
    if (!kgId) return;
    
    try {
      setLoading(true);
      const data = await getVacancyReport(kgId);
      setReportData(data || []);
    } catch (error) {
      console.error('Error loading vacancy report:', error);
      //toast.error('Помилка завантаження звіту');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [kgId]);

  // Сортировка
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return reportData;
    
    return [...reportData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      // Для числовых значений
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Для строковых значений
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [reportData, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  // Экспорт отчета
  const handleExport = async (format) => {
    if (!kgId) return;
    
    try {
      setExporting(true);
      await exportVacancyReport(kgId, format);
      //toast.success(`Звіт експортовано у форматі ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting report:', error);
      //toast.error('Помилка експорту звіту');
    } finally {
      setExporting(false);
    }
  };

  // Статистика
  const totals = React.useMemo(() => {
    return reportData.reduce((acc, item) => ({
      totalUnits: acc.totalUnits + item.totalUnits,
      occupiedUnits: acc.occupiedUnits + item.occupiedUnits,
      vacantUnits: acc.vacantUnits + item.vacantUnits
    }), { totalUnits: 0, occupiedUnits: 0, vacantUnits: 0 });
  }, [reportData]);

  return (
    <>
      <main className="queuetable-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Панель заголовка */}
            <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="queuetable-label text-white-50">Звіт по вакансіям</span>
              </div>
              <div className="queuetable-switch-wrap d-flex align-items-center gap-3">
                <button
                  className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                  onClick={loadReport}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faRefresh} className={loading ? 'fa-spin' : ''} />
                  Оновити
                </button>
                
                <div className="dropdown">
                  <button
                    className="btn btn-primary btn-sm dropdown-toggle d-flex align-items-center gap-2"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    disabled={exporting || reportData.length === 0}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    Експорт
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handleExport('csv')}
                        disabled={exporting}
                      >
                        <FontAwesomeIcon icon={faFileCsv} />
                        CSV формат
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handleExport('xlsx')}
                        disabled={exporting}
                      >
                        <FontAwesomeIcon icon={faFileExcel} />
                        Excel формат
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Карточка с таблицей */}
            <div className="card queuetable-table-card">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Завантаження...</span>
                    </div>
                    <div className="mt-2">Завантаження звіту...</div>
                  </div>
                ) : reportData.length === 0 ? (
                  <div className="text-center p-4">
                    <div className="text-muted">Немає даних для відображення</div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table queuetable-table align-middle mb-0">
                        <thead>
                          <tr>
                            {columns.map((column) => (
                              <th 
                                key={column.key}
                                style={{ width: column.width }}
                                onClick={() => column.sortable && handleSort(column.key)}
                                className={column.sortable ? 'sortable-column' : ''}
                              >
                                <div className="d-flex align-items-center">
                                  {column.label}
                                  {column.sortable && (
                                    <FontAwesomeIcon 
                                      icon={getSortIcon(column.key)} 
                                      className="ms-1" 
                                      style={{ fontSize: '12px', opacity: 0.7 }}
                                    />
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedData.map((item, index) => (
                            <tr key={`${item.positionId}-${index}`}>
                              <td>
                                <div className="vacancy-position-name">
                                  <strong>{item.positionName}</strong>
                                </div>
                              </td>
                              <td>
                                <span className={`vacancy-type-badge vacancy-type-${item.positionType?.toLowerCase()}`}>
                                  {item.positionType}
                                </span>
                              </td>
                              <td className="vacancy-number">{item.totalUnits}</td>
                              <td className="vacancy-number">{item.occupiedUnits}</td>
                              <td className="vacancy-number">
                                <span className={item.vacantUnits > 0 ? 'vacancy-highlight' : ''}>
                                  {item.vacantUnits}
                                </span>
                              </td>
                            </tr>
                          ))}
                          
                          {/* Строка итогов */}
                          <tr className="vacancy-total-row">
                            <td colSpan="2">
                              <strong>Всього по садочку:</strong>
                            </td>
                            <td className="vacancy-number">
                              <strong>{totals.totalUnits}</strong>
                            </td>
                            <td className="vacancy-number">
                              <strong>{totals.occupiedUnits}</strong>
                            </td>
                            <td className="vacancy-number">
                              <strong className={totals.vacantUnits > 0 ? 'vacancy-highlight' : ''}>
                                {totals.vacantUnits}
                              </strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Статистика */}
                    <div className="vacancy-stats p-3 border-top">
                      <div className="row">
                        <div className="col-md-4">
                          <div className="vacancy-stat-card">
                            <div className="stat-value">{reportData.length}</div>
                            <div className="stat-label">Посад у штатному розкладі</div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="vacancy-stat-card">
                            <div className="stat-value">{totals.occupiedUnits}</div>
                            <div className="stat-label">Заповнених штатних одиниць</div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="vacancy-stat-card">
                            <div className={`stat-value ${totals.vacantUnits > 0 ? 'text-warning' : 'text-success'}`}>
                              {totals.vacantUnits}
                            </div>
                            <div className="stat-label">Вакантних штатних одиниць</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Toast уведомления */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default VacancyReport;